import {
  VStack,
  HStack,
  Button,
  useBoolean,
  InputGroup,
  Input,
  InputRightElement,
  IconButton,
  SimpleGrid,
  Text,
  Tooltip,
} from '@wegroup/design-system';
import { parse } from 'query-string';
import React, { useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import QuestionBox from '../Hopper/QuestionBox';
import { generateAnswers } from './AnswerGenerator';
import { IconActionClose } from 'wg-fe-ui';
import cogoToast from 'cogo-toast';
import {
  useFlowsDispatch,
  useFlowsSelector,
} from '../../../../../common/src/utils/globalVariables';
import {
  addVisitedTag,
  updateAnswers,
} from '../../../store/redux/slices/flowsSlice';
import { FlowsQuestionGroup } from '../../../types/FlowsTypes';
import { useFlowsRouteParams } from '../../../hooks/useFlowsRouteParams';
import { sendFlowAnswerByFlowId } from '../../../services/apiService';

const Progressor: React.FC<React.PropsWithChildren<unknown>> = () => {
  const history = useHistory();
  const location = useLocation();
  const { sessionId } = useParams<{ sessionId: string }>();
  const { lastRoute } = parse(location.search);
  const [isLoading, setIsLoading] = useBoolean();
  const [isLoadingId, setIsLoadingId] = useState('');
  const [filterText, setFilterText] = useState('');
  const { flowId } = useFlowsRouteParams();

  const dispatch = useFlowsDispatch();
  const initialQuestionnaire = useFlowsSelector(
    (state) => state.flows.initialQuestionnaire,
  );
  const questionnaire = useFlowsSelector((state) => state.flows.questionnaire);
  const outputUrl = useFlowsSelector((state) => state.flows.metaData.outputUrl);

  const fullProgress = async () => {
    setIsLoading.on();
    if (!initialQuestionnaire) return;
    const answers = generateAnswers(initialQuestionnaire);
    answers.forEach((answer) => {
      dispatch(updateAnswers(answer));
    });

    const [, status] = await sendFlowAnswerByFlowId(flowId || '', sessionId, {
      answers,
    });

    if (status !== 200)
      return cogoToast.error(
        "Couldn't progress to the end, check network activity",
      );

    setIsLoading.off();

    return history.push(outputUrl);
  };

  const progressTillId = async (groupId: string) => {
    setIsLoadingId(groupId);
    if (!initialQuestionnaire) return;
    const groupIndex = initialQuestionnaire.groups.findIndex(
      ({ id }) => id === groupId,
    );
    const slicedQuestionnaire = {
      ...initialQuestionnaire,
      groups: initialQuestionnaire.groups.slice(0, groupIndex + 1),
    };

    const answers = generateAnswers(slicedQuestionnaire);
    answers.forEach((answer) => {
      dispatch(updateAnswers(answer));
      dispatch(addVisitedTag(answer.id.split('.')[0] || ''));
    });

    const [resp] = await sendFlowAnswerByFlowId(flowId || '', sessionId, {
      answers,
    });

    setIsLoadingId('');

    const newGroupIndex = questionnaire.groups.findIndex(
      ({ id }) => id === groupId,
    );

    history.push(`/flows/${flowId}/questions/${sessionId}/${newGroupIndex}`);

    return resp;
  };

  const questionnaireFilter = (group: FlowsQuestionGroup) => {
    if (!filterText) return true;
    if (group.id.includes(filterText)) {
      return true;
    } else if (group.questions.some(({ id }) => id.includes(filterText))) {
      return true;
    } else if (group.tag.includes(filterText)) {
      return true;
    }
    return false;
  };

  return (
    <VStack>
      <HStack justify="space-between" w="100%">
        <Button
          onClick={() =>
            history.push(
              `/flows/${flowId}/devtools/${sessionId}/overview?lastRoute=${lastRoute}`,
            )
          }
        >
          Back to overview
        </Button>
        <Tooltip hasArrow label="Finish the flow completely with valid data.">
          <Button onClick={fullProgress} isLoading={isLoading}>
            Finish the flow
          </Button>
        </Tooltip>
      </HStack>
      <InputGroup>
        <Input
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          placeholder="Search..."
        />
        <InputRightElement width="4.5rem">
          <IconButton
            variant="tertiary"
            onClick={() => setFilterText('')}
            aria-label="Clear search input"
            icon={<IconActionClose />}
          />
        </InputRightElement>
      </InputGroup>
      <Text my="4" textStyle="h5Headline" as="h5">
        Click any question to let the flow progress to. All answers will be
        submitted until that question.
      </Text>
      <SimpleGrid w="100%" columns={2}>
        {questionnaire.groups
          .filter(questionnaireFilter)
          .map(({ id, tag, questions }, index) => {
            return (
              <QuestionBox
                key={`group_${id}`}
                tag={tag}
                groupId={id}
                questions={questions}
                onClick={() => {
                  progressTillId(id);
                }}
                isLoading={isLoadingId === id}
              />
            );
          })}
      </SimpleGrid>
    </VStack>
  );
};

export default Progressor;
